import * as custom_hooks from "@/assets/js/hooks.js";

/**
 * Service de calcul des taxes
 * Gère le calcul des taxes selon les règles spécifiques de chaque module
 */
class TaxService {

    constructor() {
        // Le service est un singleton, pas besoin d'initialisation
    }

    /**
     * Constantes pour les seuils de prix
     */
    static get PRICE_THRESHOLDS() {
        return {
            MINIMUM: 0,
            LOW: 9,
            MEDIUM: 100,
            HIGH: 200
        };
    }

    /**
     * Constantes pour les taux de taxe par défaut
     */
    static get DEFAULT_TAX_RATES() {
        return {
            LOW_PERCENTAGE: 7,
            MEDIUM_PERCENTAGE: 6,
            HIGH_PERCENTAGE: 5,
            MINIMUM_TAX: 0.5,
            MINIMUM_TAX_THRESHOLD: 1.2
        };
    }

    /**
     * Mapping des modules avec leurs fonctions de calcul personnalisées
     */
    static get CUSTOM_TAX_MODULES() {
        return new Map([
            [63, { handler: custom_hooks.compute_tax_TSV24, requiresBooking: false }],
            [229, { handler: custom_hooks.compute_tax_TSV25, requiresBooking: false }],
            [402, { handler: custom_hooks.compute_tax_TSV26, requiresBooking: true }],
            [68, { handler: custom_hooks.compute_tax_CAPVAR24, requiresBooking: false }],
            [418, { handler: custom_hooks.compute_tax_XCOAIX, requiresBooking: false }],
            [233, { handler: custom_hooks.compute_tax_CAPVAR25, requiresBooking: false }],
            [85, { handler: custom_hooks.compute_tax_TMB24, requiresBooking: false }],
            [177, { handler: custom_hooks.compute_tax_VTTCAV24, requiresBooking: false }],
            [96, { handler: custom_hooks.compute_tax_ENR24, requiresBooking: true }],
            [250, { handler: custom_hooks.compute_tax_CVRIB25, requiresBooking: false }],
            [337, { handler: custom_hooks.compute_tax_MAXIRACE26, requiresBooking: false }],
            [397, { handler: custom_hooks.compute_tax_CALAD26, requiresBooking: false }],
            [458, { handler: custom_hooks.compute_tax_RUNFEST26, requiresBooking: false }]
        ]);
    }

    /**
     * Calcule la taxe à appliquer selon le module
     * 
     * @param {number|string} prix - Le prix sur lequel calculer la taxe
     * @param {number|string} id_module - L'ID du module
     * @param {Object} booking - La réservation complète (optionnel, nécessaire pour certains modules comme ENR24)
     * @returns {Promise<number>} Le montant de la taxe arrondi à 2 décimales
     * @throws {Error} Si les paramètres sont invalides
     */
    async computeTax(prix, id_module, booking = null) {

        // Validation et conversion des paramètres
        const normalizedPrice = this._normalizePrice(prix);
        const normalizedModuleId = this._normalizeModuleId(id_module);

        // Vérifier si le module a une fonction de calcul personnalisée
        const customTaxConfig = TaxService.CUSTOM_TAX_MODULES.get(normalizedModuleId);

        if (customTaxConfig && booking) {
            return this._computeCustomTax(customTaxConfig, normalizedPrice, booking);
        }

        // Calcul par défaut
        return this._computeDefaultTax(normalizedPrice);
    }

    /**
     * Normalise et valide le prix
     * 
     * @private
     * @param {number|string} prix - Le prix à normaliser
     * @returns {number} Le prix normalisé
     */
    _normalizePrice(prix) {
        const normalized = parseFloat(prix);

        if (isNaN(normalized)) {
            console.warn(`Prix invalide: ${prix}, utilisation de 0`);
            return 0;
        }

        if (normalized < 0) {
            console.warn(`Prix négatif: ${prix}, utilisation de 0`);
            return 0;
        }

        return normalized;
    }

    /**
     * Normalise et valide l'ID du module
     * 
     * @private
     * @param {number|string} id_module - L'ID du module à normaliser
     * @returns {number} L'ID du module normalisé
     */
    _normalizeModuleId(id_module) {
        const normalized = parseInt(id_module);

        if (isNaN(normalized)) {
            console.warn(`ID module invalide: ${id_module}, utilisation de 0`);
            return 0;
        }

        return normalized;
    }

    /**
     * Calcule la taxe avec une fonction personnalisée
     * 
     * @private
     * @param {Object} config - Configuration du module avec handler et requiresBooking
     * @param {number} prix - Le prix normalisé
     * @param {Object} booking - La réservation complète
     * @returns {number} Le montant de la taxe
     */
    _computeCustomTax(config, prix, booking) {
        const { handler, requiresBooking } = config;

        // Vérifier si le booking est requis mais non fourni
        if (requiresBooking && !booking) {
            console.warn('Ce module nécessite le booking complet pour calculer la taxe');
            return 0;
        }

        try {
            // Appeler la fonction personnalisée avec les bons paramètres
            const tax = requiresBooking
                ? handler(prix, booking)
                : handler(prix);

            // S'assurer que le résultat est un nombre valide
            const normalizedTax = parseFloat(tax);
            return isNaN(normalizedTax) ? 0 : parseFloat(normalizedTax.toFixed(2));
        } catch (error) {
            console.error('Erreur lors du calcul de la taxe personnalisée:', error);
            return 0;
        }
    }

    /**
     * Calcule la taxe avec la logique par défaut
     * 
     * @private
     * @param {number} prix - Le prix normalisé
     * @returns {number} Le montant de la taxe arrondi à 2 décimales
     */
    _computeDefaultTax(prix) {
        const { PRICE_THRESHOLDS, DEFAULT_TAX_RATES } = TaxService;

        // Si le prix est à 0, la taxe est de 0
        if (prix === PRICE_THRESHOLDS.MINIMUM) {
            return 0;
        }

        // Si le prix est inférieur à 9, la taxe est de 0,5€
        if (prix < PRICE_THRESHOLDS.LOW) {
            return DEFAULT_TAX_RATES.MINIMUM_TAX;
        }

        // Si le prix est compris entre 9 et 100, la taxe est de 7% ou 1,2€, ce qu'il y a de plus élevé
        if (prix >= PRICE_THRESHOLDS.LOW && prix < PRICE_THRESHOLDS.MEDIUM) {
            const taxe = (prix * DEFAULT_TAX_RATES.LOW_PERCENTAGE) / 100;
            const finalTaxe = Math.max(DEFAULT_TAX_RATES.MINIMUM_TAX_THRESHOLD, taxe);
            return parseFloat(finalTaxe.toFixed(2));
        }

        // Si le prix est compris entre 100 et 200, la taxe est de 6%
        if (prix >= PRICE_THRESHOLDS.MEDIUM && prix < PRICE_THRESHOLDS.HIGH) {
            const taxe = (prix * DEFAULT_TAX_RATES.MEDIUM_PERCENTAGE) / 100;
            return parseFloat(taxe.toFixed(2));
        }

        // Si le prix est supérieur ou égal à 200, la taxe est de 5%
        if (prix >= PRICE_THRESHOLDS.HIGH) {
            const taxe = (prix * DEFAULT_TAX_RATES.HIGH_PERCENTAGE) / 100;
            return parseFloat(taxe.toFixed(2));
        }

        return 0;
    }

    /**
     * Vérifie si un module a une taxe personnalisée
     * 
     * @param {number} id_module - L'ID du module
     * @returns {boolean} True si le module a une taxe personnalisée
     */
    hasCustomTax(id_module) {
        const normalizedModuleId = this._normalizeModuleId(id_module);
        return TaxService.CUSTOM_TAX_MODULES.has(normalizedModuleId);
    }

    /**
     * Récupère la liste des modules avec taxe personnalisée
     * 
     * @returns {Array<number>} Liste des IDs de modules avec taxe personnalisée
     */
    getCustomTaxModules() {
        return Array.from(TaxService.CUSTOM_TAX_MODULES.keys());
    }
}

export default new TaxService();